import React, { useMemo } from 'react';

import { TopTemplatesHeader } from 'src/components/community/TopTemplatesHeader';
import { EmptyState } from 'src/components/EmptyState';
import { TemplateIcon } from 'src/components/icons/TemplateIcon';
import type { Apollo } from 'src/gql/generated';
import { learnHowToContributeUrl, openLink } from 'src/helpers';
import { useCommunityTemplates, useSelectedCommunityCategory } from 'src/hooks/workflow/community';
import { makeStyles } from 'tss-react/mui';

import type { CommunityTemplateCardTemplate } from './CommunityTemplateCard';
import { CommunityTemplateCard, CommunityTemplateLoadingCard } from './CommunityTemplateCard';

const useStyles = makeStyles()(theme => ({
  gridContainer: {
    display: 'flex',
    flexGrow: 1,
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  icon: {
    height: 32,
    width: 32,
  },
  topTemplatesContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
  },
}));

export function CommunityTemplateBody(): JSX.Element {
  const { selectedCategory } = useSelectedCommunityCategory();
  const { communityTemplates, loading } = useCommunityTemplates({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const sortedFilteredTemplates = useMemo(() => {
    const sortedTemplates = [...communityTemplates].sort(
      (a, b) => b.numTimesImported - a.numTimesImported
    );
    if (!selectedCategory) return sortedTemplates;

    return sortedTemplates.filter(t =>
      t.communityCategories.some(c => c.id === selectedCategory.id)
    );
  }, [communityTemplates, selectedCategory]);

  if (!sortedFilteredTemplates.length)
    return <EmptyCommunityTemplates selectedCategory={selectedCategory} />;

  return <TopTemplates loading={loading} templates={sortedFilteredTemplates} />;
}

interface TopTemplatesProps {
  loading: boolean;
  templates: CommunityTemplateCardTemplate[];
}

export function TopTemplates({ loading, templates }: TopTemplatesProps): JSX.Element {
  const { classes } = useStyles();
  if (loading)
    return (
      <div className={classes.topTemplatesContainer}>
        <TopTemplatesHeader variant="template" />
        <div className={classes.gridContainer}>
          {Array.from({ length: 3 }).map((_v, index) => {
            return <CommunityTemplateLoadingCard key={index} />;
          })}
        </div>
      </div>
    );

  return (
    <div className={classes.topTemplatesContainer}>
      <TopTemplatesHeader variant="template" />
      <div className={classes.gridContainer}>
        {templates.map(template => (
          <CommunityTemplateCard key={template.id} template={template} />
        ))}
      </div>
    </div>
  );
}

interface EmptyCommunityTemplatesProps {
  selectedCategory: Pick<Apollo.CommunityCategory, 'title' | '__typename'> | null;
}

export function EmptyCommunityTemplates({
  selectedCategory,
}: EmptyCommunityTemplatesProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <EmptyState
      buttonProps={{ onClick: () => openLink(learnHowToContributeUrl) }}
      buttonText="Learn How to Contribute"
      icon={<TemplateIcon className={classes.icon} />}
      title={selectedCategory ? `No ${selectedCategory.title} Templates` : 'No Community Templates'}
    />
  );
}
